import { forwardRef, useImperativeHandle, useState } from 'react';

import { Button, Checkbox, Flex, Text } from '@raystack/apsara';
import { Modal } from '@studio/components';
import { AxiosResponse } from 'axios';

import { For, Show } from '@src/hoc';
import { Result, downloadAsset } from '@src/utils/helpers/assetHelpers';

import styles from './styles.module.css';

type downloadItem = {
  id: string;
  title: string;
  description?: string;
};

type downloadGroupSchema = {
  [key: string]: downloadItem[];
};

interface DownloadPopup {
  filename: string;
  downloadGroupSchema: downloadGroupSchema;
  startDownloadFn: () => Promise<AxiosResponse<Result>>;
  fetchStatusFn: (download_id: string) => Promise<AxiosResponse<Result>>;
  onCheckedChange: (value: string, group: string) => void;
}
type ButtonProps = React.ComponentProps<typeof Button>;

export const DownloadPopup = forwardRef(
  (
    {
      filename,
      downloadGroupSchema = {},
      startDownloadFn,
      fetchStatusFn,
      onCheckedChange
    }: DownloadPopup,
    ref
  ) => {
    const [open, setOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [checkedList, setCheckedList] = useState<string[]>([]);

    useImperativeHandle(
      ref,
      () => ({
        setShowDialog: () => setOpen(true)
      }),
      []
    );

    const handleExport = () => {
      setIsLoading(true);
      downloadAsset({
        fileName: filename,
        onSuccess: () => setOpen(false),
        onFinally: () => setIsLoading(false),
        startDownloadFn: startDownloadFn,
        fetchStatusFn: fetchStatusFn
      });
    };

    const onCheckHandler = (id: string, group: string) => {
      setCheckedList(prevSelected => {
        if (prevSelected.includes(id)) {
          // Remove the item if it is already selected
          return prevSelected.filter(item => item !== id);
        } else {
          // Add the item if it is not selected
          return [...prevSelected, id];
        }
      });
      onCheckedChange(id, group);
    };

    return (
      <Modal
        show={open}
        title='Export'
        buttonText='Export'
        loaderText='Preparing for download'
        loading={isLoading}
        disabled={isLoading || checkedList.length === 0}
        onClick={handleExport}
        onClose={() => setOpen(false)}
      >
        <Flex className={styles.dialogBody} direction='column' gap='medium'>
          <For each={Object.keys(downloadGroupSchema)}>
            {group => (
              <Flex direction='column' align='stretch' gap='small'>
                <Show when={downloadGroupSchema[group].length > 0}>
                  <Flex direction='column' gap={2}>
                    <Text size={1} variant='secondary' weight={500}>
                      {group}
                    </Text>
                    <Show when={group === 'Outputs'}>
                      <Text variant='secondary' size='micro'>
                        Includes Geotiff, CSV, JPEG files
                      </Text>
                    </Show>
                  </Flex>
                </Show>
                <For each={downloadGroupSchema[group]}>
                  {item => (
                    <Flex gap='small' align='center'>
                      <Checkbox
                        id={item.id}
                        checked={checkedList.includes(item.id)}
                        onCheckedChange={() => onCheckHandler(item.id, group)}
                      />
                      <Text size={2} weight={400} variant='primary'>
                        {item.title}
                      </Text>
                    </Flex>
                  )}
                </For>
              </Flex>
            )}
          </For>
        </Flex>
      </Modal>
    );
  }
);

DownloadPopup.displayName = 'DownloadPopup';
